if (!customElements.get('lashnova-hero-video')) {
  class LashNovaHeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.button = this.querySelector('[data-ln-hero-video-toggle]');
      this.controlLabel = this.querySelector('[data-ln-hero-video-control-label]');
      this.videos = Array.from(this.querySelectorAll('.ln-film-card__video'));
      this.posterImage = this.querySelector('.ln-film-card__image');

      if (!this.button || !this.controlLabel || this.videos.length === 0) return;

      this.initialized = true;
      this.userPaused = false;
      this.manualPlayback = false;
      this.autoplayBlocked = false;
      this.playbackFailed = false;
      this.isBuffering = false;
      this.isVisible = !('IntersectionObserver' in window);
      this.posterReady = !this.posterImage;
      this.recovering = false;
      this.recoveryAttempts = 0;
      this.maxRecoveryAttempts = 2;
      this.stallDelay = 2500;
      this.watchdogInterval = 1000;
      this.progressEpsilon = 0.05;
      this.lastObservedTime = 0;
      this.lastProgressAt = Date.now();
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleToggle = this.togglePlayback.bind(this);
      this.handleViewportChange = this.syncActiveVideo.bind(this);
      this.handleMotionChange = this.syncMotionPreference.bind(this);
      this.handleVisibilityChange = this.syncPageVisibility.bind(this);
      this.handleAutoplayRetry = this.retryPlaybackFromInteraction.bind(this);
      this.handlePlaybackChange = this.onPlaybackChange.bind(this);
      this.handlePlaybackProgress = this.onPlaybackProgress.bind(this);
      this.handlePlaybackStall = this.onPlaybackStall.bind(this);
      this.handlePlaybackError = this.onPlaybackError.bind(this);
      this.handlePosterReady = this.preparePosterPlayback.bind(this);

      if (this.posterImage) {
        this.posterImage.addEventListener('load', this.handlePosterReady);
        this.posterImage.addEventListener('error', this.handlePosterReady);
        if (this.posterImage.complete) this.preparePosterPlayback();
      }

      this.button.addEventListener('click', this.handleToggle);
      this.videos.forEach((video) => {
        video.autoplay = false;
        video.defaultMuted = true;
        video.muted = true;
        video.playsInline = true;
        video.loop = false;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.addEventListener('play', this.handlePlaybackChange);
        video.addEventListener('pause', this.handlePlaybackChange);
        video.addEventListener('ended', this.handlePlaybackChange);
        video.addEventListener('playing', this.handlePlaybackProgress);
        video.addEventListener('timeupdate', this.handlePlaybackProgress);
        video.addEventListener('waiting', this.handlePlaybackStall);
        video.addEventListener('stalled', this.handlePlaybackStall);
        video.addEventListener('error', this.handlePlaybackError);
      });

      this.addQueryListener(this.mobileQuery, this.handleViewportChange);
      this.addQueryListener(this.reducedMotionQuery, this.handleMotionChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;

            this.isVisible = entry.isIntersecting;
            if (this.isVisible) {
              this.attemptPlayback();
            } else if (this.activeVideo) {
              this.stopPlaybackMonitoring();
              this.activeVideo.pause();
            }
            this.updateControl();
          },
          { rootMargin: '120px 0px', threshold: 0.01 }
        );
        this.visibilityObserver.observe(this);
      }

      this.syncActiveVideo();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.stopPlaybackMonitoring();
      this.clearAutoplayRetry();
      if (this.posterImage) {
        this.posterImage.removeEventListener('load', this.handlePosterReady);
        this.posterImage.removeEventListener('error', this.handlePosterReady);
      }
      this.posterPreparation = null;
      window.cancelAnimationFrame(this.posterFrame);
      this.button.removeEventListener('click', this.handleToggle);
      this.videos.forEach((video) => {
        video.pause();
        video.removeEventListener('play', this.handlePlaybackChange);
        video.removeEventListener('pause', this.handlePlaybackChange);
        video.removeEventListener('ended', this.handlePlaybackChange);
        video.removeEventListener('playing', this.handlePlaybackProgress);
        video.removeEventListener('timeupdate', this.handlePlaybackProgress);
        video.removeEventListener('waiting', this.handlePlaybackStall);
        video.removeEventListener('stalled', this.handlePlaybackStall);
        video.removeEventListener('error', this.handlePlaybackError);
      });
      this.removeQueryListener(this.mobileQuery, this.handleViewportChange);
      this.removeQueryListener(this.reducedMotionQuery, this.handleMotionChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      this.initialized = false;
    }

    addQueryListener(query, listener) {
      if (query.addEventListener) query.addEventListener('change', listener);
      else query.addListener(listener);
    }

    removeQueryListener(query, listener) {
      if (query.removeEventListener) query.removeEventListener('change', listener);
      else query.removeListener(listener);
    }

    getActiveVideo() {
      if (this.mobileQuery.matches) {
        return (
          this.videos.find((video) => video.classList.contains('ln-film-card__video--mobile')) ||
          this.videos.find((video) => !video.classList.contains('ln-film-card__video--desktop'))
        );
      }

      return this.videos.find((video) => !video.classList.contains('ln-film-card__video--mobile'));
    }

    preparePosterPlayback() {
      if (this.posterReady || this.posterPreparation) return;

      // Let the eager responsive image decode and paint before auto-starting media.
      // Explicit playback remains immediate; a failed image must not disable video.
      const preparation = {};
      this.posterPreparation = preparation;
      const decoded = this.posterImage?.decode ? this.posterImage.decode() : Promise.resolve();
      decoded.catch(() => {}).then(() => {
        if (!this.isConnected || this.posterPreparation !== preparation) return;
        this.posterFrame = window.requestAnimationFrame(() => {
          this.posterFrame = window.requestAnimationFrame(() => {
            if (!this.isConnected || this.posterPreparation !== preparation) return;
            this.posterReady = true;
            this.posterPreparation = null;
            this.attemptPlayback();
          });
        });
      });
    }

    canKeepPlaying() {
      if (
        !this.isConnected ||
        !this.activeVideo ||
        this.userPaused ||
        this.playbackFailed ||
        (!this.posterReady && !this.manualPlayback) ||
        !this.isVisible ||
        document.visibilityState === 'hidden'
      ) {
        return false;
      }

      return !this.reducedMotionQuery.matches || this.manualPlayback;
    }

    resetPlaybackRecovery() {
      this.stopPlaybackMonitoring();
      this.playPromise = null;
      this.recovering = false;
      this.recoveryAttempts = 0;
      this.autoplayBlocked = false;
      this.playbackFailed = false;
      this.isBuffering = false;
      this.lastObservedTime = this.activeVideo ? this.activeVideo.currentTime || 0 : 0;
      this.lastProgressAt = Date.now();
    }

    syncActiveVideo() {
      const nextVideo = this.getActiveVideo();
      const activeVideoChanged = this.activeVideo !== nextVideo;

      this.stopPlaybackMonitoring();
      this.clearAutoplayRetry();
      this.videos.forEach((video) => {
        const isActive = video === nextVideo;
        video.autoplay = false;
        video.preload = 'none';
        video.loop = false;
        if (!isActive) video.pause();
      });

      this.activeVideo = nextVideo;
      this.button.hidden = !this.activeVideo;

      if (!this.activeVideo) return;

      if (activeVideoChanged) {
        this.manualPlayback = false;
        this.resetPlaybackRecovery();
        this.activeVideo.style.visibility = '';
      }

      this.activeVideo.defaultMuted = true;
      this.activeVideo.muted = true;
      this.activeVideo.playsInline = true;
      this.activeVideo.setAttribute('muted', '');
      this.activeVideo.setAttribute('playsinline', '');
      this.activeVideo.setAttribute('webkit-playsinline', '');
      this.activeVideo.loop = !this.reducedMotionQuery.matches;

      if (!this.canKeepPlaying()) {
        this.activeVideo.pause();
        this.updateControl();
        return;
      }

      this.attemptPlayback();
    }

    attemptPlayback(userInitiated = false, fromRecovery = false) {
      if (userInitiated) this.manualPlayback = true;

      if (!this.canKeepPlaying()) {
        this.updateControl();
        return;
      }

      const video = this.activeVideo;
      if (!video || this.playPromise) return;

      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.loop = !this.reducedMotionQuery.matches;

      const deferredSources = video.querySelectorAll('source[data-src]');
      if (deferredSources.length) {
        deferredSources.forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.load();
      }

      let playPromise;
      try {
        playPromise = video.play();
      } catch (error) {
        this.handlePlayFailure(video, userInitiated, fromRecovery);
        return;
      }

      if (!playPromise || typeof playPromise.then !== 'function') {
        this.playbackStarted(video);
        return;
      }

      this.playPromise = playPromise;
      this.lastObservedTime = video.currentTime || 0;
      this.lastProgressAt = Date.now();
      this.startProgressWatchdog();
      playPromise.then(
        () => {
          if (this.playPromise === playPromise) this.playPromise = null;
          if (video !== this.activeVideo) return;
          this.playbackStarted(video);
        },
        () => {
          if (this.playPromise === playPromise) this.playPromise = null;
          this.handlePlayFailure(video, userInitiated, fromRecovery);
        }
      );
    }

    playbackStarted(video) {
      if (video !== this.activeVideo) return;

      if (!this.canKeepPlaying()) {
        this.stopPlaybackMonitoring();
        video.pause();
        this.updateControl();
        return;
      }

      this.autoplayBlocked = false;
      this.playbackFailed = false;
      this.isBuffering = false;
      this.recovering = false;
      video.removeAttribute('data-ln-awaiting-frame');
      video.style.visibility = '';
      this.clearAutoplayRetry();
      this.notePlaybackProgress(true);
      this.startProgressWatchdog();
      this.updateControl();
    }

    handlePlayFailure(video, userInitiated, fromRecovery) {
      if (video !== this.activeVideo) return;

      this.recovering = false;
      this.isBuffering = false;
      this.stopPlaybackMonitoring();

      if ((userInitiated || fromRecovery) && this.recoveryAttempts < this.maxRecoveryAttempts) {
        this.scheduleImmediateRecovery('play_rejected');
        return;
      }

      this.autoplayBlocked = true;
      this.playbackFailed = true;
      video.style.visibility = 'hidden';
      this.armAutoplayRetry();
      this.updateControl();
    }

    onPlaybackChange(event) {
      if (event.target !== this.activeVideo) return;

      if (event.type === 'play') {
        this.lastObservedTime = this.activeVideo.currentTime || 0;
        this.lastProgressAt = Date.now();
        this.startProgressWatchdog();
      } else if (event.type === 'pause' && !this.recovering) {
        this.stopProgressWatchdog();
      } else if (event.type === 'ended' && this.canKeepPlaying()) {
        this.activeVideo.currentTime = 0;
        this.attemptPlayback(this.manualPlayback);
      }

      this.updateControl();
    }

    onPlaybackProgress(event) {
      if (event.target !== this.activeVideo) return;

      if (event.type === 'playing') {
        this.playbackStarted(this.activeVideo);
        return;
      }

      this.notePlaybackProgress();
    }

    notePlaybackProgress(force = false) {
      if (!this.activeVideo) return;

      const currentTime = Number(this.activeVideo.currentTime) || 0;
      const progressed = force || Math.abs(currentTime - this.lastObservedTime) >= this.progressEpsilon;
      if (!progressed) return;

      this.lastObservedTime = currentTime;
      this.lastProgressAt = Date.now();
      this.isBuffering = false;
      this.clearStallRecovery();
      this.updateControl();
    }

    onPlaybackStall(event) {
      if (event.target !== this.activeVideo || !this.canKeepPlaying()) return;

      this.isBuffering = true;
      this.updateControl();
      this.scheduleStallRecovery(event.type);
    }

    onPlaybackError(event) {
      if (event.target !== this.activeVideo || !this.canKeepPlaying()) return;

      this.isBuffering = true;
      this.updateControl();
      this.scheduleImmediateRecovery('media_error');
    }

    startProgressWatchdog() {
      this.stopProgressWatchdog();
      if (!this.canKeepPlaying()) return;

      this.progressWatchdog = window.setInterval(() => {
        if (!this.canKeepPlaying()) {
          this.stopProgressWatchdog();
          return;
        }

        const currentTime = Number(this.activeVideo.currentTime) || 0;
        if (Math.abs(currentTime - this.lastObservedTime) >= this.progressEpsilon) {
          this.notePlaybackProgress();
          return;
        }

        if (!this.activeVideo.paused && Date.now() - this.lastProgressAt >= this.stallDelay) {
          this.isBuffering = true;
          this.updateControl();
          this.recoverPlayback('watchdog');
        }
      }, this.watchdogInterval);
    }

    stopProgressWatchdog() {
      if (!this.progressWatchdog) return;
      window.clearInterval(this.progressWatchdog);
      this.progressWatchdog = null;
    }

    scheduleStallRecovery(reason) {
      if (this.stallRecoveryTimer || !this.canKeepPlaying()) return;

      const watchedVideo = this.activeVideo;
      this.stallRecoveryTimer = window.setTimeout(() => {
        this.stallRecoveryTimer = null;
        if (watchedVideo !== this.activeVideo || !this.canKeepPlaying()) return;

        const currentTime = Number(this.activeVideo.currentTime) || 0;
        if (Math.abs(currentTime - this.lastObservedTime) >= this.progressEpsilon) {
          this.notePlaybackProgress();
          return;
        }

        this.recoverPlayback(reason);
      }, this.stallDelay);
    }

    scheduleImmediateRecovery(reason) {
      this.clearStallRecovery();
      const watchedVideo = this.activeVideo;
      this.stallRecoveryTimer = window.setTimeout(() => {
        this.stallRecoveryTimer = null;
        if (watchedVideo !== this.activeVideo || !this.canKeepPlaying()) return;
        this.recoverPlayback(reason);
      }, 250);
    }

    clearStallRecovery() {
      if (!this.stallRecoveryTimer) return;
      window.clearTimeout(this.stallRecoveryTimer);
      this.stallRecoveryTimer = null;
    }

    recoverPlayback(reason) {
      if (this.recovering || !this.canKeepPlaying()) return;

      // A slow first download is not a stalled playing video. Reloading it after
      // 2.5 seconds throws away useful buffering on a slow mobile connection.
      if (this.activeVideo.readyState < 2 && this.activeVideo.currentTime === 0 &&
          reason !== 'media_error' && reason !== 'play_rejected') return;

      if (this.recoveryAttempts >= this.maxRecoveryAttempts) {
        this.showPlaybackFallback();
        return;
      }

      const video = this.activeVideo;
      this.recoveryAttempts += 1;
      this.recovering = true;
      this.isBuffering = true;
      this.playPromise = null;
      this.clearStallRecovery();
      this.stopProgressWatchdog();
      video.pause();

      if (this.recoveryAttempts === this.maxRecoveryAttempts) {
        const mp4Source = Array.from(video.querySelectorAll('source')).find(
          (source) => source.type === 'video/mp4' || source.src.toLowerCase().includes('.mp4')
        );
        if (mp4Source) {
          video.src = mp4Source.src;
          video.dataset.lnMp4Fallback = 'true';
        }
      }

      video.preload = 'auto';
      video.load();
      this.lastObservedTime = 0;
      this.lastProgressAt = Date.now();
      this.attemptPlayback(this.manualPlayback, true);
      this.recovering = false;
    }

    showPlaybackFallback() {
      if (!this.activeVideo) return;

      this.stopPlaybackMonitoring();
      this.recovering = false;
      this.isBuffering = false;
      this.autoplayBlocked = true;
      this.playbackFailed = true;
      this.activeVideo.pause();
      this.activeVideo.style.visibility = 'hidden';
      this.button.hidden = false;
      this.updateControl();
    }

    stopPlaybackMonitoring() {
      this.clearStallRecovery();
      this.stopProgressWatchdog();
    }

    armAutoplayRetry() {
      if (!this.activeVideo || this.retryVideo === this.activeVideo || this.playbackFailed) return;

      this.clearAutoplayRetry();
      this.retryVideo = this.activeVideo;
      this.retryVideo.addEventListener('pointerdown', this.handleAutoplayRetry, {
        once: true,
        passive: true,
      });
    }

    clearAutoplayRetry() {
      if (!this.retryVideo) return;
      this.retryVideo.removeEventListener('pointerdown', this.handleAutoplayRetry);
      this.retryVideo = null;
    }

    retryPlaybackFromInteraction() {
      this.retryVideo = null;
      if (!this.autoplayBlocked || this.userPaused) return;
      this.prepareManualRetry();
      this.attemptPlayback(true);
    }

    prepareManualRetry() {
      this.stopPlaybackMonitoring();
      this.playPromise = null;
      this.recoveryAttempts = 0;
      this.recovering = false;
      this.autoplayBlocked = false;
      this.playbackFailed = false;
      this.isBuffering = false;
      if (this.activeVideo) this.activeVideo.style.visibility = '';
      this.lastObservedTime = this.activeVideo ? this.activeVideo.currentTime || 0 : 0;
      this.lastProgressAt = Date.now();
    }

    syncPageVisibility() {
      if (!this.activeVideo) return;

      if (document.visibilityState === 'hidden') {
        this.stopPlaybackMonitoring();
        this.activeVideo.pause();
        this.updateControl();
        return;
      }

      this.attemptPlayback();
    }

    syncMotionPreference() {
      if (this.reducedMotionQuery.matches) {
        this.stopPlaybackMonitoring();
        this.clearAutoplayRetry();
        this.videos.forEach((video) => {
          video.autoplay = false;
          video.loop = false;
          video.pause();
        });
        this.userPaused = false;
        this.manualPlayback = false;
        this.autoplayBlocked = false;
        this.playbackFailed = false;
        this.isBuffering = false;
        this.updateControl();
        return;
      }

      this.syncActiveVideo();
    }

    togglePlayback() {
      if (!this.activeVideo) return;

      if (this.activeVideo.paused || this.activeVideo.ended || this.playbackFailed) {
        if (this.activeVideo.ended) this.activeVideo.currentTime = 0;
        this.userPaused = false;
        this.manualPlayback = true;
        this.clearAutoplayRetry();
        this.prepareManualRetry();
        this.attemptPlayback(true);
      } else {
        this.userPaused = true;
        this.manualPlayback = false;
        this.autoplayBlocked = false;
        this.playbackFailed = false;
        this.isBuffering = false;
        this.recovering = false;
        this.playPromise = null;
        this.stopPlaybackMonitoring();
        this.clearAutoplayRetry();
        this.activeVideo.pause();
      }
    }

    updateControl() {
      if (!this.activeVideo) return;

      const isPlaying =
        !this.activeVideo.paused &&
        !this.activeVideo.ended &&
        !this.isBuffering &&
        !this.playbackFailed;

      if (this.isBuffering && !this.playbackFailed) {
        this.button.dataset.state = 'loading';
        this.button.setAttribute('aria-label', 'Loading hero video');
        this.button.setAttribute('aria-busy', 'true');
        this.controlLabel.textContent = 'Loading video';
        return;
      }

      this.button.removeAttribute('aria-busy');
      this.button.dataset.state = isPlaying ? 'playing' : 'paused';
      this.button.setAttribute('aria-label', isPlaying ? 'Pause hero video' : 'Play hero video');
      this.controlLabel.textContent = isPlaying ? 'Pause video' : 'Play video';
    }
  }

  customElements.define('lashnova-hero-video', LashNovaHeroVideo);
}
